
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useCallback, useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { useQueryClient } from "@tanstack/react-query";
import { Textarea } from "@/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Plus } from "lucide-react";

const AVAILABLE_SECTIONS = [
  "Personal details",
  "Summary",
  "Technical skills",
  "Work experience",
  "Projects",
  "Education",
  "Certifications"
];

type FormValues = {
  email: string;
};

const AddUserFromResumeModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [hasValidFile, setHasValidFile] = useState(false);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newSection, setNewSection] = useState("");
  const [rawJson, setRawJson] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const form = useForm<FormValues>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const tryParsing = useCallback(async (email: string) => {
    try {
      setIsParsing(true);
      const file = fileInputRef.current?.files?.[0];
      if (!file) {
        throw new Error('No file selected');
      }

      // Convert file to FormData
      const formData = new FormData();
      if(file) formData.append('file', file);
      if(email) formData.append('email', email);
      if(rawJson) formData.append('rawJson', rawJson || '');
      if(selectedSections) formData.append('sections', JSON.stringify(selectedSections));
      formData.append('shouldCreateUser', 'true');

      const response = await supabase.functions.invoke('parse-resume', {
        body: formData,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to parse resume');
      }
      const parsedData = response.data.data;
      console.log('Parsed resume data:', parsedData);

      // Invalidate all relevant queries to refresh the dashboard data
      await queryClient.invalidateQueries({ queryKey: ['users'] });

      // Close modal on success
      setIsOpen(false);
      form.reset();
      setSelectedSections([]);
      setRawJson("");
      setHasValidFile(false);

      toast({
        title: "Success",
        description: "User has been created from resume",
      });

    } catch (error) {
      console.error('Error parsing resume:', error);
      toast({
        title: "Upload failed",
        description: "There was an error processing the resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsParsing(false);
    }
  }, [selectedSections, rawJson, queryClient, toast, form]);

  const handleAddSection = (section: string) => {
    if (section === "All sections") {
      const unselectedSections = AVAILABLE_SECTIONS.filter(s => !selectedSections.includes(s));
      setSelectedSections([...selectedSections, ...unselectedSections]);
    } else if (!selectedSections.includes(section)) {
      setSelectedSections([...selectedSections, section]);
    }
    setNewSection("");
    setShowSuggestions(false);
  };

  const handleRemoveSection = (section: string) => {
    setSelectedSections(selectedSections.filter(s => s !== section));
  };

  const onSubmit = async (data: FormValues) => {
    await tryParsing(data.email);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add User from Resume
        </Button>
      </DialogTrigger>
      <DialogContent className="overflow-visible"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}>
        <DialogHeader>
          <DialogTitle>Add User from Resume</DialogTitle>
          <DialogDescription>
            Upload a resume to create a new user account. The resume will be parsed to populate the user's profile.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                disabled={isUploading || isParsing}
                onChange={(newFile) => setHasValidFile(newFile.target.files.length > 0)}
                className="cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
              {(isUploading || isParsing) &&
                <p className="text-sm text-muted-foreground pt-2">
                  {isParsing ? "Parsing... (could take up to 2 minutes)" : "Uploading..."}
                </p>
              }
            </div>

            <div className="space-y-2">
              <Label>Raw JSON (Optional)</Label>
              <Textarea
                placeholder="Paste your JSON data here..."
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
                className="min-h-[100px]"
                disabled={isUploading || isParsing}
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-1 block">
                Sections to automatically update from upload
              </Label>
              <div className="relative" ref={dropdownRef}>
                <Input
                  value={newSection}
                  onChange={(e) => {
                    setNewSection(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onClick={() => setShowSuggestions(true)}
                  placeholder="Add section..."
                />
                {showSuggestions && (
                  <div className="absolute w-full z-50">
                    <Command className="rounded-lg border shadow-md">
                      <CommandList>
                        <CommandEmpty>No section found.</CommandEmpty>
                        <CommandGroup className="max-h-[200px] overflow-auto">
                          {selectedSections.length < AVAILABLE_SECTIONS.length && (
                            <CommandItem
                              value="All sections"
                              onSelect={handleAddSection}
                              className="cursor-pointer font-medium"
                            >
                              All sections
                            </CommandItem>
                          )}
                          {AVAILABLE_SECTIONS
                            .filter(section =>
                              section.toLowerCase().includes(newSection.toLowerCase()) &&
                              !selectedSections.includes(section)
                            )
                            .map((section) => (
                              <CommandItem
                                key={section}
                                value={section}
                                onSelect={handleAddSection}
                                className="cursor-pointer"
                              >
                                {section}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedSections.map((section) => (
                  <Badge
                    key={section}
                    variant="secondary"
                    className="cursor-pointer group"
                    onClick={() => handleRemoveSection(section)}
                  >
                    {section} <span className="group-hover:text-red-500 pl-1">×</span>
                  </Badge>
                ))}
              </div>
            </div>

            <DialogFooter className="flex justify-between sm:justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                disabled={isUploading || isParsing}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUploading || isParsing || (!hasValidFile && !rawJson)}
              >
                {isParsing ? "Parsing..." : isUploading ? "Uploading..." : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserFromResumeModal;

